// =============================================================================
// io/sync/assembleImported.ts — a parsed trade → a saveable Trade.
// -----------------------------------------------------------------------------
// The gap `buildImportedTrade` deliberately leaves. Its own docstring says what
// it will not do:
//
//   "DELIBERATELY NOT HERE… id generation, account resolution, session
//    derivation, and applying the firm commission schedule. Those need app state
//    or a clock; keeping them out is what makes this function testable at all.
//    The caller assembles the Trade."
//
// This is that caller. It lives in `io/` rather than `domain/` for exactly the
// reason named there: it needs an id, which needs entropy and a clock.
//
// ── ⚠ ID GENERATION IS INJECTED ──
//
// `newId` is a parameter with a default, not an inlined `Math.random()`. A
// deterministic id in a test is the difference between asserting the assembled
// row and asserting around it — and the whole point of this module is that the
// row is assertable.
//
// ── WHAT IT DOES NOT DO: HEAL THE FEE ──
//
// `selectors/derive.ts` applies the commission schedule at the READ boundary for
// every surface (D59). Applying it again here would write a healed fee into the
// database — which is what the old client does, and which D59 deliberately
// declined to copy. So `feesAuto` is carried through and the fee is left as the
// source supplied it. The import writes what the file said; the read shows what
// the schedule implies.
//
// ⚠ That is a REAL DIVERGENCE from the old client and it is deliberate. Recorded
// here rather than discovered later: the stored `fees` on a newly imported trade
// will be the source's, not the healed one, and every surface still shows the
// healed figure because it derives on read.
// =============================================================================

import type { ImportedTrade } from '../parsers/sierra';
import type { Account, Trade } from '../../domain/types';
import { sessionDateFor } from '../../domain/session';
import { findContract, type ContractSpec } from '../../domain/trade';

/** `Math.random` + a clock, matching the old client's `uid()` shape. */
export function newTradeId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

export interface AssembleContext {
  accounts: readonly Account[];
  contracts: readonly ContractSpec[];
  /** `prefs.sessionTimezone` — decides which ET hour a clock time maps to. */
  sessionTz: string;
  /**
   * Where these trades go.
   *
   * `null` means AUTO-DETECT: match `sourceAccount` against `accounts.externalId`
   * case-insensitively, exactly as the ingest endpoint does. An explicit id
   * routes every trade in the file to that account regardless of what it says.
   */
  accountId: string | null;
  newId?: () => string;
}

export interface AssembleResult {
  trades: Trade[];
  /**
   * Rows that could not be assembled, with why.
   *
   * ⚠ REPORTED, not dropped. An import that silently discards 40 of 200 rows and
   * says "imported 160" is how a user comes to trust a book that is missing a
   * fifth of their trading.
   */
  rejected: { row: ImportedTrade; reason: string }[];
  /** Trades whose account could not be resolved. They still import, unassigned. */
  unassigned: number;
}

/**
 * Assemble parsed rows into saveable trades.
 *
 * ⚠ An unresolvable ACCOUNT is not a rejection — the trade imports with
 * `accountId: null` and shows under "Unassigned", which is a real filter option
 * for exactly this case. Rejecting it would lose the trade entirely over a
 * mismatched account string.
 *
 * ⚠ An unresolvable CONTRACT *is* a rejection, and it stays one now that the
 * foreign key behind it is gone. `trades.contract` used to reference
 * `contracts.symbol`, so one unknown row failed the WHOLE batch — beta hit that
 * repeatedly, under the name "batch poison" — and this per-row check existed to
 * get ahead of it. Migration `20260906000006` drops the constraint so a trader
 * can add their own instruments, which means THIS is now the only thing standing
 * between an unpriceable symbol and a $0 trade in the book. It matters more than
 * it did, not less.
 *
 * ▶ And the rejection is no longer a dead end: the message names Settings →
 * Trade → Contract Library, which can add the instrument and let the file
 * through on a second attempt.
 */
export function assembleImported(
  rows: readonly ImportedTrade[],
  ctx: AssembleContext,
): AssembleResult {
  const id = ctx.newId ?? newTradeId;
  const trades: Trade[] = [];
  const rejected: { row: ImportedTrade; reason: string }[] = [];
  let unassigned = 0;

  // Built once — an import of 900 rows must not re-scan the account list 900 times.
  const byExternal = new Map<string, Account>();
  for (const a of ctx.accounts) {
    if (a.externalId) byExternal.set(a.externalId.trim().toLowerCase(), a);
  }

  for (const r of rows) {
    // ── the contract, which has to be priceable ───────────────────────────────
    const spec = findContract(r.contract, ctx.contracts);
    if (!spec) {
      // 🚨 THE MESSAGE NAMES THE FIX NOW, because there finally is one. This
      // used to end at "would fail the whole batch on the foreign key", which
      // was true, was the real reason, and left the trader with a rejected file
      // and nowhere to go — the instrument could only be added by a developer
      // writing a migration. Settings → Trade → Contract Library adds it.
      rejected.push({
        row: r,
        reason: `"${r.contract}" is not in your contract list, so its trades cannot be ` +
          `priced. Add the instrument under Settings → Trade → Contract Library and ` +
          `import the file again.`,
      });
      continue;
    }

    // ── the account ───────────────────────────────────────────────────────────
    let accountId: string | null = ctx.accountId;
    if (!accountId) {
      const key = (r.sourceAccount ?? '').trim().toLowerCase();
      accountId = key ? (byExternal.get(key)?.id ?? null) : null;
    }
    if (!accountId) unassigned++;

    // ── the session date, derived not guessed ─────────────────────────────────
    // ⚠ The same rule every read surface uses. Writing a null here would leave
    // the trade dependent on derive.ts forever; writing the CALENDAR date would
    // be actively wrong for an evening trade.
    const sessionDate = sessionDateFor(r.date, r.timeIn, ctx.sessionTz) || null;

    trades.push({
      id: id(),
      accountId,
      date: r.date,
      sessionDate,
      timeIn: r.timeIn || null,
      timeOut: r.timeOut || null,
      // ⚠ ROOT in `contract` (the FK), month-bearing symbol in `contractFull` —
      // the same split the ingest endpoint uses, so the exact-contract bar fetch
      // can later ask for the instrument actually traded.
      contract: spec.symbol,
      contractFull: r.contractFull ?? r.contract ?? null,
      side: r.side,
      qty: r.qty,
      entry: r.entry,
      exit: r.exit,
      points: r.points,
      // ⚠ NO `dollarsPerPoint`. The column exists but beta strips it on load —
      // "orphaned core.dollarsPerPoint… now derived via dppForTrade(); storage was
      // redundant" — and the Trade type omits it, so writing it would be dropped
      // by TRADE_COLUMNS regardless. $/point comes from the catalogue on read.
      grossPnl: r.grossPnl,
      fees: r.fees,
      feesAuto: r.feesAuto,
      netPnl: r.netPnl,
      maePoints: r.maePoints,
      mfePoints: r.mfePoints,
      maeSecs: r.maeSecs,
      mfeSecs: r.mfeSecs,
      // ⚠ `annotated: false` puts it in the Needs Review queue, and `imported:
      // true` marks its provenance. Both match the ingest endpoint — an imported
      // trade counts for every quantitative metric immediately and waits only for
      // its qualitative tags.
      annotated: false,
      imported: true,
      sourceAccount: r.sourceAccount,
      syntheticClose: false,
      // ── 🚨 TOP-LEVEL, not nested under `extras` ─────────────────────────────
      //
      // This read `{ extras: { executions } }` and it was WRONG in a way no test
      // could see. `extras` is not a `Trade` field, so it is not in
      // TRADE_KNOWN_KEYS — which means `extractExtras` treats the whole `extras`
      // object as an unknown key and stores `extras = {"extras":{"executions":…}}`.
      // `tradeFromDb` then spreads that back and the trade has an `extras`
      // property instead of an `executions` one, so the Fills table on the
      // Execution tab was empty for every trade this client imported.
      //
      // ⚠ The old test asserted `t.extras.executions` on the IN-MEMORY object,
      // which is true of the broken shape and of nothing else — the recurring
      // defect exactly: an assertion that is true, of a case that cannot expose
      // the bug. It now asserts the tradeToDb → tradeFromDb round trip, which is
      // the only thing the database sees.
      ...(r.executions?.length ? { executions: r.executions } : {}),
    } as unknown as Trade);
  }

  return { trades, rejected, unassigned };
}
